import { getDirectRelationship } from '@cognite/apm-client';
import type {
  Action,
  Condition,
  ConditionalAction,
  ConditionalActionsResponseType,
  ConditionalActionsReturnType,
  ConditionalActionUpsertType,
  IAPMAppDataService,
  InFieldUser,
  ViewVersions,
} from '@cognite/apm-client';
import type { CogniteClientDependencies } from '@cognite/fdm-client';
import { FDMClient } from '@cognite/fdm-client';

import type { IConditionalActionsService } from './conditional-actions-service-interface';

export type APMClientDependencies = {
  conditionalActions: Pick<
    IAPMAppDataService<ConditionalAction>,
    'upsert' | 'delete'
  >;
  appDataModelSpace: string;
  appDataInstanceSpace: string;
  userInstanceSpace: string;
  viewVersions: Pick<
    ViewVersions,
    'CONDITIONAL_ACTION' | 'CONDITION' | 'ACTION'
  >;
};

export class ConditionalActionsService
  extends FDMClient
  implements IConditionalActionsService
{
  apmClient: APMClientDependencies;
  modelSpace: string;
  instanceSpace: string;
  BASE_URL: string;

  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.apmClient = apmClient;
    this.modelSpace = apmClient.appDataModelSpace;
    this.instanceSpace = apmClient.appDataInstanceSpace;
    this.BASE_URL = `${this.cogniteClient.getBaseUrl()}/api/v1/projects/${
      this.cogniteClient.project
    }`;
  }

  async createConditionalActions(
    conditionalActions: ConditionalAction[],
    user: InFieldUser
  ) {
    const upsertedConditionalActions: ConditionalAction[] =
      conditionalActions.map((conditionalAction) => ({
        ...conditionalAction,
        createdBy: user,
      }));
    return this.upsertConditionalActions(upsertedConditionalActions).then(
      (result) => result.data.items[0]
    );
  }

  async updateConditionalActions(
    conditionalActions: ConditionalAction[],
    user: InFieldUser
  ) {
    const upsertedConditionalActions: ConditionalAction[] =
      conditionalActions.map((conditionalAction) => ({
        ...conditionalAction,
        updatedBy: user,
      }));

    return this.upsertConditionalActions(upsertedConditionalActions);
  }

  async upsertConditionalActions(conditionalActions: ConditionalAction[]) {
    const upsertedConditionalActions: ConditionalActionUpsertType[] =
      conditionalActions.map((conditionalAction) => {
        const parentObjectRelationship = getDirectRelationship(
          conditionalAction.parentObject
        );
        const createdByRelationship = getDirectRelationship({
          externalId: conditionalAction.createdBy?.externalId,
          space: this.apmClient.userInstanceSpace,
        });
        const updatedByRelationship = getDirectRelationship({
          externalId: conditionalAction.updatedBy?.externalId,
          space: this.apmClient.userInstanceSpace,
        });

        return {
          ...conditionalAction,
          parentObject: parentObjectRelationship,
          createdBy: createdByRelationship,
          updatedBy: updatedByRelationship,
        };
      });
    return this.apmClient.conditionalActions.upsert(upsertedConditionalActions);
  }

  async deleteConditionalActions(externalIds: string[]) {
    return this.apmClient.conditionalActions.delete(externalIds);
  }

  async getConditionalActionsByParentObject(
    externalIds: string[],
    limit = 1500
  ) {
    const instanceIds = externalIds.map((externalId) => ({
      space: this.instanceSpace,
      externalId,
    }));

    const data = {
      with: {
        conditionalActions: {
          limit,
          sort: [
            {
              property: ['node', 'externalId'],
              direction: 'ascending',
            },
          ],
          nodes: {
            filter: {
              and: [
                {
                  equals: {
                    property: ['node', 'space'],
                    value: this.instanceSpace,
                  },
                },
                {
                  in: {
                    property: [
                      this.modelSpace,
                      `ConditionalAction/${this.apmClient.viewVersions.CONDITIONAL_ACTION}`,
                      'parentObject',
                    ],
                    values: instanceIds,
                  },
                },
                {
                  hasData: [
                    {
                      type: 'container',
                      space: this.modelSpace,
                      externalId: 'ConditionalAction',
                    },
                  ],
                },
              ],
            },
          },
        },
        conditions: {
          limit,
          sort: [
            {
              property: ['node', 'externalId'],
              direction: 'ascending',
            },
          ],
          nodes: {
            from: 'conditionalActions',
            direction: 'inwards',
            through: {
              view: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Condition',
                version: this.apmClient.viewVersions.CONDITION,
              },
              identifier: 'conditionalAction',
            },
            filter: {
              and: [
                {
                  equals: {
                    property: ['node', 'space'],
                    value: this.instanceSpace,
                  },
                },
                {
                  hasData: [
                    {
                      type: 'container',
                      space: this.modelSpace,
                      externalId: 'Condition',
                    },
                  ],
                },
              ],
            },
          },
        },
        actions: {
          limit,
          sort: [
            {
              property: ['node', 'externalId'],
              direction: 'ascending',
            },
          ],
          nodes: {
            from: 'conditionalActions',
            direction: 'inwards',
            through: {
              view: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Action',
                version: this.apmClient.viewVersions.ACTION,
              },
              identifier: 'conditionalActions',
            },
            filter: {
              and: [
                {
                  equals: {
                    property: ['node', 'space'],
                    value: this.instanceSpace,
                  },
                },
                {
                  hasData: [
                    {
                      type: 'container',
                      space: this.modelSpace,
                      externalId: 'Action',
                    },
                  ],
                },
              ],
            },
          },
        },
      },
      select: {
        conditionalActions: {
          sources: [
            {
              source: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'ConditionalAction',
                version: this.apmClient.viewVersions.CONDITIONAL_ACTION,
              },
              properties: ['*'],
            },
          ],
        },
        conditions: {
          sources: [
            {
              source: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Condition',
                version: this.apmClient.viewVersions.CONDITION,
              },
              properties: ['*'],
            },
          ],
        },
        actions: {
          sources: [
            {
              source: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Action',
                version: this.apmClient.viewVersions.ACTION,
              },
              properties: ['*'],
            },
          ],
        },
      },
    };

    return this.cogniteClient
      .post<{ items: ConditionalActionsResponseType }>(
        `${this.BASE_URL}/models/instances/query`,
        {
          data,
          withCredentials: true,
        }
      )
      .then((result) => {
        const data: ConditionalActionsResponseType = result.data.items;

        // Return only properties
        const conditionalActions: ConditionalAction[] =
          data.conditionalActions?.map((conditionalAction) => {
            return {
              ...conditionalAction.properties[this.modelSpace][
                `ConditionalAction/${this.apmClient.viewVersions.CONDITIONAL_ACTION}`
              ],
              externalId: conditionalAction.externalId,
            };
          });
        const conditions: Condition[] = data.conditions?.map((condition) => {
          return {
            ...condition.properties[this.modelSpace][
              `Condition/${this.apmClient.viewVersions.CONDITION}`
            ],
            externalId: condition.externalId,
          };
        });
        const actions: Action[] = data.actions?.map((action) => {
          return {
            ...action.properties[this.modelSpace][
              `Action/${this.apmClient.viewVersions.ACTION}`
            ],
            externalId: action.externalId,
          };
        });

        // Map conditions and actions to conditional actions
        const conditionalActionsMapping: ConditionalAction[] =
          conditionalActions.map((conditionalAction) => {
            return {
              ...conditionalAction,
              conditions: conditions.filter(
                (condition) =>
                  condition.conditionalAction?.externalId ===
                  conditionalAction.externalId
              ),
              actions: actions.filter(
                (action) =>
                  action.conditionalActions?.externalId ===
                  conditionalAction.externalId
              ),
            };
          });

        return {
          conditionalActions: conditionalActionsMapping,
          conditions,
          actions,
        } as ConditionalActionsReturnType;
      });
  }

  async getConditionalActions(externalIds: string[], limit = 1500) {
    const data = {
      with: {
        conditionalActions: {
          limit,
          nodes: {
            filter: {
              and: [
                {
                  equals: {
                    property: ['node', 'space'],
                    value: this.instanceSpace,
                  },
                },
                {
                  in: {
                    property: ['node', 'externalId'],
                    values: externalIds,
                  },
                },
              ],
            },
          },
        },
        conditions: {
          limit,
          nodes: {
            from: 'conditionalActions',
            direction: 'inwards',
            through: {
              view: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Condition',
                version: this.apmClient.viewVersions.CONDITION,
              },
              identifier: 'conditionalAction',
            },
          },
        },
        actions: {
          limit,
          nodes: {
            from: 'conditionalActions',
            direction: 'inwards',
            through: {
              view: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Action',
                version: this.apmClient.viewVersions.ACTION,
              },
              identifier: 'conditionalActions',
            },
          },
        },
      },
      select: {
        conditionalActions: {
          sources: [
            {
              source: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'ConditionalAction',
                version: this.apmClient.viewVersions.CONDITIONAL_ACTION,
              },
              properties: ['*'],
            },
          ],
        },
        conditions: {
          sources: [
            {
              source: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Condition',
                version: this.apmClient.viewVersions.CONDITION,
              },
              properties: ['*'],
            },
          ],
        },
        actions: {
          sources: [
            {
              source: {
                type: 'view',
                space: this.modelSpace,
                externalId: 'Action',
                version: this.apmClient.viewVersions.ACTION,
              },
              properties: ['*'],
            },
          ],
        },
      },
    };

    return this.cogniteClient
      .post<{ items: ConditionalActionsResponseType }>(
        `${this.BASE_URL}/models/instances/query`,
        {
          data,
          withCredentials: true,
        }
      )
      .then((result) => {
        const data: ConditionalActionsResponseType = result.data.items;

        // Return only properties
        const conditionalActions: ConditionalAction[] =
          data.conditionalActions?.map((conditionalAction) => {
            return {
              ...conditionalAction.properties[this.modelSpace][
                `ConditionalAction/${this.apmClient.viewVersions.CONDITIONAL_ACTION}`
              ],
              externalId: conditionalAction.externalId,
            };
          });
        const conditions: Condition[] = data.conditions?.map((condition) => {
          return {
            ...condition.properties[this.modelSpace][
              `Condition/${this.apmClient.viewVersions.CONDITION}`
            ],
            externalId: condition.externalId,
          };
        });
        const actions: Action[] = data.actions?.map((action) => {
          return {
            ...action.properties[this.modelSpace][
              `Action/${this.apmClient.viewVersions.ACTION}`
            ],
            externalId: action.externalId,
          };
        });

        // Map conditions and actions to conditional actions
        const conditionalActionsMapping: ConditionalAction[] =
          conditionalActions.map((conditionalAction) => {
            return {
              ...conditionalAction,
              conditions: conditions.filter(
                (condition) =>
                  condition.conditionalAction?.externalId ===
                  conditionalAction.externalId
              ),
              actions: actions.filter(
                (action) =>
                  action.conditionalActions?.externalId ===
                  conditionalAction.externalId
              ),
            };
          });

        return {
          conditionalActions: conditionalActionsMapping,
          conditions,
          actions,
        } as ConditionalActionsReturnType;
      });
  }
}

export const createConditionalActionsService = (
  ...args: ConstructorParameters<typeof ConditionalActionsService>
): IConditionalActionsService => new ConditionalActionsService(...args);
